import styles from '../RequestDetails.module.scss';
import Card from 'components/Card/Card';
import CardContent from 'components/CardContent';
import DateTimePicker from 'components/DateTimePicker';
import {useLayoutEffect, useState} from 'react';
import Select from 'components/Select';
import {format} from 'date-fns';
import {GoogleDriveFile, RequestDetails} from 'types';
import Morning from 'pages/DeliveryPlanner/icons/morning';
import Afternoon from 'pages/DeliveryPlanner/icons/afternoon';
import Evening from 'pages/DeliveryPlanner/icons/evening';
import {components} from 'react-select';
import PriorityFlag from 'components/PriorityFlag';
import {ScriptStatusOption} from 'components/ScriptStatusSelect';
import useCompanyConfig from 'hooks/useCompanyConfig';
import Typography from 'components/Typography';
import Attachments from 'components/Attachments';
import {
  PRIORITY_OPTIONS,
  PRIORITY_OPTIONS_MAP,
  STATUS_OPTIONS,
  STATUS_OPTIONS_MAP,
  TIME_OPTIONS,
  TIME_OPTIONS_MAP,
} from 'constants/select-options';

interface Props {
  details?: RequestDetails;
  onChangePriority: (priority: any) => void;
  onChangeTime: ({
    preference_date,
    preference_time,
  }: {
    preference_date?: string | null;
    preference_time?: string | null;
  }) => void;
  status: string;
  onChangeStatus: (newValue: any, actionMeta: any) => void;
  drugId: number;
  googleDriveFiles?: GoogleDriveFile[];
}

function PreferenceTime({
  onChangeTime,
  onChangePriority,
  details,
  status,
  onChangeStatus,
  drugId,
  googleDriveFiles,
}: Props) {
  const preference_date = details?.preference_date;
  const preference_time = details?.preference_time;
  const priority = details?.priority;
  const description = details?.description;
  const orderType = details?.order_type;

  const {labels} = useCompanyConfig();

  const [optimisticState, setOptimisticState] = useState({
    status: STATUS_OPTIONS_MAP[status],
    priority: PRIORITY_OPTIONS_MAP[priority as string],
    preference_date: preference_date ? new Date(preference_date) : null,
    preference_time: TIME_OPTIONS_MAP[preference_time as string],
  });

  useLayoutEffect(() => {
    setOptimisticState({
      status: STATUS_OPTIONS_MAP[status],
      priority: PRIORITY_OPTIONS_MAP[priority as string],
      preference_date: preference_date ? new Date(preference_date) : null,
      preference_time: TIME_OPTIONS_MAP[preference_time as string],
    });
  }, [status, priority, preference_date, preference_time]);

  const handleOptimisticState = (
    key: keyof typeof optimisticState,
    value: any
  ) => {
    setOptimisticState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleChangePreferenceDate = (date: any) => {
    handleOptimisticState('preference_date', date ? new Date(date) : null);
    onChangeTime({
      preference_date: date ? format(date, 'yyyy-MM-dd') : null,
    });
  };

  const handleChangePreferenceTime = (nextVal: any) => {
    handleOptimisticState(
      'preference_time',
      TIME_OPTIONS_MAP[nextVal?.value as string]
    );
    onChangeTime({
      preference_time: nextVal?.value,
    });
  };

  const handleChangePriority = (newValue: any) => {
    handleOptimisticState(
      'priority',
      PRIORITY_OPTIONS_MAP[newValue?.value as string]
    );
    onChangePriority(newValue?.value);
  };

  const handleChangeStatus = (newValue: any) => {
    handleOptimisticState(
      'status',
      STATUS_OPTIONS_MAP[newValue?.value as string]
    );
    onChangeStatus(newValue as ScriptStatusOption, {
      name: `drug-${drugId}`,
    });
  };

  return (
    <Card className={styles.card}>
      <CardContent pt="md">
        <div className={styles.sectionHeader}>
          <h2 className={styles.sectionTitle}>
            {orderType === 'pickup' && 'Purchase '}
            {orderType === 'dropoff' && 'Delivery '}
            {labels.order} Details
          </h2>
        </div>
        <div className={styles.times}>
          <div style={{flex: 1}}>
            <div className={styles.label}>Status:</div>
            <Select
              size="md"
              placeholder="Select status"
              value={optimisticState.status}
              onChange={handleChangeStatus}
              options={STATUS_OPTIONS}
            />
          </div>
          <div style={{flex: 1}}>
            <div className={styles.label}>Priority:</div>
            <Select
              placeholder="Select Priority"
              onChange={handleChangePriority}
              value={optimisticState.priority}
              className={styles.priority}
              options={PRIORITY_OPTIONS}
              isClearable
              components={{
                SingleValue: (props) => {
                  return (
                    <components.SingleValue
                      {...props}
                      className={styles.select_option}
                    >
                      {props.data.label}
                      <PriorityFlag priority={props.data.value as any} />
                    </components.SingleValue>
                  );
                },
                Option: (props) => {
                  return (
                    <components.Option
                      {...props}
                      className={styles.select_option}
                    >
                      {props.data.label}
                      <PriorityFlag priority={props.data.value as any} />
                    </components.Option>
                  );
                },
              }}
            />
          </div>
        </div>
        <div className={styles.times}>
          <div style={{flex: 1}}>
            <div className={styles.label}>Preference Date:</div>
            <div className={styles.calendarRoot}>
              <DateTimePicker
                showTimeSelect={false}
                dateFormat="MM/dd/yy E"
                value={optimisticState.preference_date}
                onChange={handleChangePreferenceDate}
              />
            </div>
          </div>
          <div style={{flex: 1}}>
            <div className={styles.label}>Preference Time:</div>
            <Select
              placeholder="Select Time"
              onChange={handleChangePreferenceTime}
              value={optimisticState.preference_time}
              isClearable
              options={TIME_OPTIONS}
              size="md"
              components={{
                Option: (props) => {
                  const icons = {
                    morning: <Morning />,
                    afternoon: <Afternoon />,
                    evening: <Evening />,
                  };
                  return (
                    <components.Option
                      {...props}
                      className={styles.select_option}
                    >
                      {props.data.label}
                      {icons[props.data.value as keyof typeof icons]}
                    </components.Option>
                  );
                },
              }}
            />
          </div>
        </div>

        {!!description && (
          <div className={styles.description}>
            <div className={styles.descriptionLabel}>
              {labels.order} Description:
            </div>
            <Typography variant="p" className={styles.descriptionText}>
              {description || 'No description'}
            </Typography>
          </div>
        )}

        {googleDriveFiles && googleDriveFiles?.length > 0 && (
          <Attachments attachments={googleDriveFiles || []} />
        )}
      </CardContent>
    </Card>
  );
}

export default PreferenceTime;
